// Shared constants & tuning — the one home for magic numbers, the palette, and
// the art/music asset URLs. Imports only engine TYPES (erased at runtime), so
// this leaf module pulls in no engine code and is safe for `verify.ts` to import.
import type { GameOptions } from '../engine/webgpu.js';

// ── THE CANVAS ───────────────────────────────────────────────────────────────
export const WORLD_HEIGHT = 768;              // logical world height, in world units
export const BACKGROUND = '#a9c4d6';          // dusty-blue clear colour behind the world
export const PIXEL_ART = false;               // smooth, painted look
export const CONTAINER = '#game';

export const GAME_OPTIONS: GameOptions = {
  container: CONTAINER,
  background: BACKGROUND,
  pixelArt: PIXEL_ART,
  worldHeight: WORLD_HEIGHT,
};

// ── GENERATED ASSETS ─────────────────────────────────────────────────────────
export const ART = {
  titleVista: 'https://gameblocks.nyc3.digitaloceanspaces.com/FHfo6MEN8qV/art/nazareth-vista/bg-c895dfb00d.png',
};
export const MUSIC = {
  theme: 'https://gameblocks.nyc3.digitaloceanspaces.com/FHfo6MEN8qV/music/a-peaceful-contemplative-cozy-crafting-g-f621bf2ed00e.mp3',
};
export const FONT = {
  loraPng: 'https://gameblocks.nyc3.cdn.digitaloceanspaces.com/gameblocks/fonts/lora.png',
  loraJson: 'https://gameblocks.nyc3.cdn.digitaloceanspaces.com/gameblocks/fonts/lora.json',
  nunitoPng: 'https://gameblocks.nyc3.cdn.digitaloceanspaces.com/gameblocks/fonts/nunito.png',
  nunitoJson: 'https://gameblocks.nyc3.cdn.digitaloceanspaces.com/gameblocks/fonts/nunito.json',
};

// ── PALETTE (warm storybook Nazareth) ────────────────────────────────────────
export const C = {
  sky: '#a9c4d6',
  grass: '#8aa04c',
  grassDark: '#799241',
  grassLight: '#9ab058',
  sand: '#d8c39a',
  sandDark: '#c3aa7c',
  path: '#d3bd92',
  water: '#5f97b0',
  waterDeep: '#3f7791',
  stoneWall: '#e9dcc0',
  stoneDark: '#cbb790',
  roof: '#b5643c',
  roofDark: '#8f4a2b',
  timber: '#8a5a34',
  timberDark: '#5b3a22',
  timberLight: '#a9743f',
  wood: '#c68a4e',
  leaf: '#6f7f3f',
  leafDark: '#57662f',
  leafLight: '#87983f',
  olive: '#7a8a4e',
  rock: '#9b9384',
  rockDark: '#77705f',
  cloth: '#c9553f',
  ink: '#3a2c1e',
  parchment: '#efe2c2',
  parchmentEdge: '#d8c49a',
  gold: '#d9a441',
  goldLight: '#f4d58d',
  lamp: '#ffcf7a',
  robeJoseph: '#9c7a4a',   // earth-toned outer garment
  tunicJoseph: '#c9b489',  // simple tunic
  robeMary: '#5f6f9c',     // dusty blue
  veilMary: '#d8ccb0',
  tunicJesus: '#d8c39a',
  skin: '#dcae82',
  skinDark: '#c69368',
  hair: '#4a3423',
  hairGray: '#6b5540',
  dusk: '#2c2a44',
  white: '#f6efdd',
} as const;

// ── HUD LAYOUT ───────────────────────────────────────────────────────────────
// The HUD is drawn in CSS pixels, so d.w / d.h change with the window: panels
// are sized from MEASURED text and clamped against the frame, never from fixed
// widths. These are the margins and bounds that keeps every readout on screen.
export const HUD = {
  margin: 12,       // gap from the frame edge, on every side
  pad: 14,          // inner padding inside a HUD panel
  gap: 16,          // minimum gap between the top-left and top-right panels
  statusW: 268,     // top-left meters panel
  statusH: 116,
  goalMinW: 200,    // top-right day / chapter / objective panel
  goalMaxW: 420,
  statMinGap: 10,   // smallest gap between the Craft/Charity/Family readouts
  lineGap: 4,       // extra leading between wrapped HUD lines
  blockGap: 8,      // extra space between the day header and the objective
} as const;

// ── PANELS ───────────────────────────────────────────────────────────────────
// Inner padding for every parchment panel. Text is wrapped to the panel's width
// MINUS twice this, measured in pixels — so nothing ever touches a border.
export const PANEL = { pad: 30 } as const;

// What the workbench costs to build. Lived in two places (state.ts and the
// prompt in play.ts) and so could drift; it is one number now.
export const WORKBENCH_COST = { wood: 6, stone: 2 } as const;

// ── WORKING ANIMALS ──────────────────────────────────────────────────────────
// The donkey and the ox follow Joseph by walking his own recent path, so they
// can never be steered into a building — he could not have stood there. These
// are the numbers for that trail and its fallbacks.
export const ANIMAL = {
  speed: 104,        // world units per second while following
  trailStep: 0.07,   // seconds between breadcrumbs
  trailLag: 1.1,     // seconds behind Joseph the animal aims for
  followGap: 46,     // how far back it settles when he stops
  snapDist: 300,     // further than this and it gives up and snaps to him
  stuckTime: 1.6,    // seconds of no progress before snapping
} as const;

// ── INTERIOR PALETTE ─────────────────────────────────────────────────────────
// Indoors runs warmer than the village outside. DESIGN.md asks for terracotta,
// olive, warm golden light and lamplight; the outdoor stone/sand tones read
// cool and chalky once they are the whole screen, so interiors get their own
// earth and plaster, pushed toward ochre and clay.
export const CI = {
  floor: '#b08a5c',        // packed earth, well trodden
  floorDark: '#8e6c44',
  floorLit: '#c8a273',
  wall: '#d8bd93',         // warm lime plaster, not cold cream
  wallShade: '#b2946c',
  wallLit: '#ecd7ac',
  beam: '#6b4526',         // roof beams overhead
  cloth: '#a8452f',        // deep terracotta — replaces the salmon
  clothDark: '#7d3020',
  clothWorn: '#b86b4a',
  blanket: '#8a6234',
  straw: '#c9a86a',
  olive: '#6d7a42',
  glaze: '#8c4a2e',        // fired clay
  brick: '#8a5c42',        // the hearth: fired brick and soot, never grey concrete
  brickDark: '#5f3d2b',
  quern: '#8a7358',        // basalt, but a warm basalt
  linen: '#c6a97b',        // sacking — oatmeal, not bleached white
} as const;

// ── INTERIORS ────────────────────────────────────────────────────────────────
// Crossing a threshold has to read as ritual, not tax: quick, no input lock, no
// camera travel. These are the numbers that keep it that way.
export const INTERIOR = {
  dip: 0.16,          // seconds of warm wash when stepping through a door
  wall: 26,           // wall thickness drawn inside the room rect
  lightFloor: 0.45,   // fraction of the outdoor night tint a lit room takes
  margin: 34,         // how close Joseph may walk to a wall
} as const;

// How far each ground region's wash fades out at its edges. A region that ends
// on a hard line reads as a rectangle painted on the map; these widths are the
// distance over which it dissolves into the grass instead.
export const REGION = {
  steps: 7,        // nested passes per wash — more steps, smoother ramp
  grove: 150,
  groveEdge: 70,
  hills: 150,
  fields: 150,
  plaza: 170,
  overlap: 110,    // how far the hills and fields wash past their shared line
  lobe: 0.55,      // size of the soft lobes that break up a feathered edge
} as const;

// ── GAMEPLAY TUNING ──────────────────────────────────────────────────────────
export const MOVE_SPEED = 230;          // world units / second
export const INTERACT_RANGE = 78;       // how close Joseph must be to interact
export const STRENGTH_MAX = 100;
export const PEACE_MAX = 100;

export const COST = {
  chop: 10,       // strength per tree chop
  rock: 12,       // strength per stone
  branch: 3,      // strength per fallen branch
  haul: 7,        // strength per heavy load, WITH an animal to bear it
  craftMajor: 8,  // strength per major craft
  craftMinor: 3,  // strength per minor craft
} as const;

export const RESTORE = {
  meal: 60,       // strength from a family meal
  water: 22,      // strength from a drink at the well
  rest: 18,       // strength from sitting quietly
  prayerPeace: 40,
  mealPeace: 20,
  helpPeace: 14,
} as const;

// Craftsmanship thresholds → workshop level (index 0 = level 1, up to level 7).
export const WORKSHOP_THRESHOLDS = [0, 30, 70, 120, 180, 260, 360] as const;

// Chapter goals.
export const CH2_REQUESTS = 5;   // village requests to finish Chapter II
export const CH3_FAMILY = 12;    // family points for Chapter III
export const CH3_HOME_PROJECTS = 2;
export const COMMUNITY_STAGES = 4;

// Day cycle: seconds of real time per full day arc (gentle; sleeping ends a day).
export const DAY_LENGTH = 200;

// Quality mini-game score → quality index (0 GOOD, 1 EXCELLENT, 2 MASTERWORK).
export const QUALITY_NAMES = ['Good', 'Excellent', 'Masterwork'] as const;

// Charset baked into every bitmap font (ASCII printable + the punctuation our
// copy uses). Missing glyphs render blank, so this must cover our text.
export const CHARSET =
  ' !"#$%&\'()*+,-./0123456789:;<=>?@ABCDEFGHIJKLMNOPQRSTUVWXYZ[\\]^_`' +
  'abcdefghijklmnopqrstuvwxyz{|}~' +
  '•—–…’‘“”·×½¼¾°éíóúüñ';

// A warm serif and a soft sans stack for the storybook UI.
export const FONT_SERIF = 'Georgia, "Times New Roman", serif';
export const FONT_SANS = '"Trebuchet MS", "Segoe UI", system-ui, sans-serif';
